import { useCallback, useEffect, useState } from 'react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Loader2 } from 'lucide-react'
import type { Set } from '@/types/cardCatalog'
import {
  fetchCardmarketExpansions,
  fetchCardmarketNonSinglesList,
  fetchCardmarketSinglesList,
  fetchSets,
  type CardmarketNonSinglesListResponse,
  type CardmarketSinglesListResponse,
} from '@/services/cardService'
import { CardMarketExpansionsTable } from './CardMarketExpansionsTable'
import { CardMarketNonSinglesTable } from './CardMarketNonSinglesTable'
import { CardMarketSinglesTable } from './CardMarketSinglesTable'

const PAGE_SIZE = 100

export function CardMarketTab() {
  const [kind, setKind] = useState<'singles' | 'nonsingles' | 'expansions'>('singles')
  const [searchInput, setSearchInput] = useState('')
  const [debouncedQ, setDebouncedQ] = useState('')
  const [offset, setOffset] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [singlesData, setSinglesData] = useState<CardmarketSinglesListResponse | null>(null)
  const [nonSinglesData, setNonSinglesData] = useState<CardmarketNonSinglesListResponse | null>(null)
  const [expansionsData, setExpansionsData] = useState<Awaited<ReturnType<typeof fetchCardmarketExpansions>> | null>(null)
  const [catalogSets, setCatalogSets] = useState<Set[]>([])

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedQ(searchInput.trim()), 350)
    return () => window.clearTimeout(t)
  }, [searchInput])

  useEffect(() => {
    setOffset(0)
  }, [debouncedQ, kind])

  useEffect(() => {
    if (kind !== 'expansions') return
    let cancelled = false
    void fetchSets({ limit: -1 })
      .then((r) => {
        if (!cancelled) setCatalogSets(r.items)
      })
      .catch(() => {
        if (!cancelled) setCatalogSets([])
      })
    return () => {
      cancelled = true
    }
  }, [kind])

  const fetchPage = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      if (kind === 'expansions') {
        const json = await fetchCardmarketExpansions({
          limit: PAGE_SIZE,
          offset,
          q: debouncedQ || undefined,
        })
        setExpansionsData(json)
        setSinglesData(null)
        setNonSinglesData(null)
      } else {
        const q = debouncedQ || undefined
        if (kind === 'singles') {
          const json = await fetchCardmarketSinglesList({ q, limit: PAGE_SIZE, offset })
          setSinglesData(json)
          setNonSinglesData(null)
        } else {
          const json = await fetchCardmarketNonSinglesList({ q, limit: PAGE_SIZE, offset })
          setNonSinglesData(json)
          setSinglesData(null)
        }
        setExpansionsData(null)
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load data')
      setSinglesData(null)
      setNonSinglesData(null)
      setExpansionsData(null)
    } finally {
      setLoading(false)
    }
  }, [kind, debouncedQ, offset])

  useEffect(() => {
    void fetchPage()
  }, [fetchPage])

  const cardmarketListMeta = kind === 'singles' ? singlesData : kind === 'nonsingles' ? nonSinglesData : null
  const meta = kind === 'singles' ? singlesData : kind === 'nonsingles' ? nonSinglesData : expansionsData
  const total = meta?.total ?? 0
  const canPrev = offset > 0
  const canNext = offset + PAGE_SIZE < total

  return (
    <div className="p-4 md:p-0 space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Cardmarket catalog</CardTitle>
          <CardDescription>
            Browse singles (with price guide), non-singles from cached product lists, or database links between Cardmarket expansion ids and your catalog sets. Product data
            refreshes when the server reloads stale files.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
            <div className="inline-flex flex-wrap rounded-lg border border-gray-200 p-1 bg-gray-50 gap-1">
              <Button type="button" variant={kind === 'singles' ? 'default' : 'ghost'} size="sm" onClick={() => setKind('singles')} className="rounded-md">
                Singles
              </Button>
              <Button type="button" variant={kind === 'nonsingles' ? 'default' : 'ghost'} size="sm" onClick={() => setKind('nonsingles')} className="rounded-md">
                Non-singles
              </Button>
              <Button type="button" variant={kind === 'expansions' ? 'default' : 'ghost'} size="sm" onClick={() => setKind('expansions')} className="rounded-md">
                Expansions (DB)
              </Button>
            </div>
            <Input
              placeholder={kind === 'expansions' ? 'Search by name or expansion id…' : 'Search by name, category, product ID, or expansion ID…'}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="max-w-md"
            />
          </div>

          {cardmarketListMeta && (
            <p className="text-sm text-muted-foreground">
              List version {cardmarketListMeta.version} · updated {cardmarketListMeta.createdAt} · showing {cardmarketListMeta.items.length} of {cardmarketListMeta.total} matches
            </p>
          )}
          {meta && kind === 'expansions' && (
            <p className="text-sm text-muted-foreground">
              Database rows · showing {expansionsData?.items.length ?? 0} of {meta.total} matches
            </p>
          )}

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {loading && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading…
            </div>
          )}

          {!loading && kind === 'singles' && singlesData && <CardMarketSinglesTable items={singlesData.items} />}

          {!loading && kind === 'nonsingles' && nonSinglesData && <CardMarketNonSinglesTable items={nonSinglesData.items} />}

          {!loading && kind === 'expansions' && expansionsData && (
            <CardMarketExpansionsTable items={expansionsData.items} catalogSets={catalogSets} onRefresh={() => void fetchPage()} />
          )}

          {meta && total > PAGE_SIZE && (
            <div className="flex items-center justify-between gap-2 pt-2">
              <Button type="button" variant="outline" size="sm" disabled={!canPrev} onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}>
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">
                {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
              </span>
              <Button type="button" variant="outline" size="sm" disabled={!canNext} onClick={() => setOffset((o) => o + PAGE_SIZE)}>
                Next
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
